import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ToastrService } from 'ngx-toastr';
import { DataService } from 'src/app/core/services/data.service';
import { HttpResponse } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.css', '../../shared/styles/common.css'],
    standalone: false
})
export class RegisterComponent implements OnInit, OnDestroy {
  registerForm: FormGroup;
  @ViewChild('otp1') otp1Input!: ElementRef;

  // Registration state
  otpSent: boolean = false;
  submitted: boolean = false;
  isLoading: boolean = false;

  // Confirmation dialog state
  showConfirmDialog: boolean = false;
  confirmEmail: string = '';
  confirmPhone: string = '';

  // User data storage
  registeredFirstName: string = '';
  registeredLastName: string = '';
  currentEmailOrMobile: string = '';
  // Mobile number entered for this registration attempt, kept separate from
  // currentEmailOrMobile (which may get switched to email depending on which
  // channel the backend used) so we can always pass both email and mobile
  // in the verify/resend OTP payload, since both receive the same OTP.
  registeredMobile: string = '';

  // Registration type - always 'mobile' now since we have separate email field
  registerType: 'email' | 'mobile' = 'mobile';

  // True once the backend has told us the email/mobile is already registered
  // and we've switched from "create account" to "log in via OTP" for this
  // attempt. Drives which API verifyOTP() calls.
  isLoginRedirectFlow: boolean = false;

  // Email entered for this registration attempt, kept so we can offer it as
  // the OTP channel if the backend responds with redirect_to === 'email'/'both'.
  private pendingEmail: string = '';

  // OTP related properties
  otpDigits: string[] = ['', '', '', '', '', ''];
  resendTimer: number = 60;
  canResend: boolean = false;

  // Message shown in the "OTP sent to your ..." banner. Set dynamically from
  // the backend response (or the redirect_to-derived message) instead of
  // being hardcoded, since the actual delivery channel can be email or
  // mobile depending on what the API decided.
  otpSentMessage: string = '';

  private timerInterval: any;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private toastr: ToastrService,
    private snackBar: MatSnackBar,
    private data: DataService
  ) {
    this.registerForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, this.validateEmail.bind(this)]],
      mobile: ['', [Validators.required, this.validateMobile.bind(this)]],
      otp: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });
  }

  ngOnInit() {
    if (this.authService.isLoggedIn()) {
      this.router.navigate(['/consumer/appointments']);
    }
  }

  ngOnDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  get f() { return this.registerForm.controls; }

  /**
   * Validate email format
   */
  validateEmail(control: FormControl): { [key: string]: boolean } | null {
    const value = control.value;
    if (!value) return null;
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (emailPattern.test(value.trim())) {
      return null;
    }
    return { invalidEmail: true };
  }

  /**
   * Validate mobile number:
   * - exactly 10 digits
   * - must start with 6, 7, 8, or 9
   * Leading zeros are stripped before validation
   */
  validateMobile(control: FormControl): { [key: string]: boolean } | null {
    const value = control.value;
    if (!value) return null;

    const cleaned = String(value).replace(/^0+/, '');
    const mobilePattern = /^[6-9]\d{9}$/;

    if (mobilePattern.test(cleaned)) {
      return null;
    }
    return { invalidMobile: true };
  }

  /**
   * Strip leading zeros from mobile input in real time
   */
  onMobileInput(event: any): void {
    let inputValue: string = event.target.value;
    // Remove non-digits
    inputValue = inputValue.replace(/\D/g, '');
    // Remove leading zeros
    inputValue = inputValue.replace(/^0+/, '');
    event.target.value = inputValue;
    this.registerForm.get('mobile')?.setValue(inputValue, { emitEvent: true });
  }

  /**
   * Handle Register button click
   */
  onRegisterButtonClicked(): void {
    this.trimFormFields();
    this.submitted = true;

    if (!this.otpSent) {
      if (!this.isInitialFormValid()) {
        return;
      }
      // Show confirmation dialog instead of calling API directly
      this.openConfirmDialog();
    } else {
      this.verifyOTP();
    }
  }

  /**
   * Open confirmation dialog with current email and mobile
   */
  openConfirmDialog(): void {
    const emailValue = this.registerForm.get('email')?.value?.trim().toLowerCase();
    const mobileValue = this.registerForm.get('mobile')?.value?.replace(/^0+/, '');
    this.confirmEmail = emailValue;
    this.confirmPhone = mobileValue;
    this.showConfirmDialog = true;
  }

  /**
   * User clicked "No, Edit" — close dialog, return to form
   */
  onConfirmCancel(): void {
    this.showConfirmDialog = false;
  }

  /**
   * User clicked "Yes, Continue" — close dialog and trigger API
   */
  onConfirmProceed(): void {
    this.showConfirmDialog = false;
    this.sendOTP();
  }

  /**
   * Check if initial form fields are valid
   */
  private isInitialFormValid(): boolean {
    return !(
      this.registerForm.get('firstName')?.invalid ||
      this.registerForm.get('lastName')?.invalid ||
      this.registerForm.get('email')?.invalid ||
      this.registerForm.get('mobile')?.invalid
    );
  }

  /**
   * Send OTP to user's mobile (called after confirmation)
   */
  private sendOTP(): void {
    this.isLoading = true;

    const firstName = this.registerForm.get('firstName')?.value?.trim();
    const lastName = this.registerForm.get('lastName')?.value?.trim();
    const email = this.registerForm.get('email')?.value?.trim().toLowerCase();
    const mobile = this.registerForm.get('mobile')?.value?.replace(/^0+/, '');

    // Store for later use
    this.registeredFirstName = firstName;
    this.registeredLastName = lastName;
    this.currentEmailOrMobile = mobile;
    this.registeredMobile = mobile;
    this.pendingEmail = email;
    this.registerType = 'mobile';
    this.isLoginRedirectFlow = false;

    // Call authService to send OTP via register API using mobile, also
    // forwarding email so the backend can detect an email-based conflict
    this.authService.sendOtpForRegistration(mobile, firstName, 'mobile', 'register', lastName, email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;
          this.handleOtpSent(response);
        },
        error: (error) => {
          this.isLoading = false;
          this.handleSendOtpError(error);
        }
      });
  }

  /**
   * Handle OTP sent successfully
   */
  private handleOtpSent(response: HttpResponse<any>): void {
    const isSuccess = response.body?.otp_sent ||
                     (response.body?.error === false) ||
                     (response.status === 200 || response.status === 201);

    if (isSuccess) {
      const msg = response.body?.msg || 'OTP sent to your mobile number';

      // The backend may deliver the OTP via email even though registration
      // was submitted with logintype 'mobile' (e.g. it prioritizes a
      // verified email when both are provided). Detect the actual channel
      // from the response message so verify/resend target the right
      // identifier, and the banner/toast reflect what really happened.
      if (msg.toLowerCase().includes('email') && this.pendingEmail) {
        this.registerType = 'email';
        this.currentEmailOrMobile = this.pendingEmail;
      }

      this.otpSent = true;
      this.submitted = false;
      this.canResend = false;
      this.resendTimer = 60;
      this.otpSentMessage = msg;

      this.toastr.success(msg, 'Success!');

      this.startResendTimer();
    } else {
      this.toastr.error('Failed to send OTP. Please try again.', 'Error!');
    }
  }

  private handleSendOtpError(error: any): void {
    const body = error?.error;
    const errorMessage = body?.message || body?.msg || 'Failed to send OTP. Please try again.';
    const redirectTo: 'email' | 'mobile' | 'both' | undefined = body?.redirect_to;

    // Already-in-use detection: prefer the structured redirect_to flag from
    // the API; fall back to the previous message-sniffing for backends/errors
    // that don't send redirect_to yet.
    const isAlreadyInUse =
      !!redirectTo ||
      errorMessage.toLowerCase().includes('already registered') ||
      errorMessage.toLowerCase().includes('already exists') ||
      errorMessage.toLowerCase().includes('already in use') ||
      errorMessage.toLowerCase().includes('already');

    if (!isAlreadyInUse) {
      // normal error handling
      this.toastr.error(errorMessage, 'Error!');
      return;
    }

    // Pick which channel to send the login OTP to, and the matching message.
    // redirect_to === 'email' or 'both' -> prioritize email.
    // redirect_to === 'mobile'          -> use mobile.
    // no redirect_to (legacy fallback)  -> keep prior behavior (mobile).
    let identifierType: 'email' | 'mobile';
    let identifierValue: string;
    let alreadyInUseMsg: string;

    if (redirectTo === 'email') {
      identifierType = 'email';
      identifierValue = this.pendingEmail;
      alreadyInUseMsg = 'Email is already registered. Please log in using the OTP sent to your inbox.';
    } else if (redirectTo === 'mobile') {
      identifierType = 'mobile';
      identifierValue = this.currentEmailOrMobile;
      alreadyInUseMsg = 'Phone number is already registered. Please log in using the OTP sent via SMS.';
    } else if (redirectTo === 'both') {
      identifierType = 'email';
      identifierValue = this.pendingEmail;
      alreadyInUseMsg = 'Account already exists. Please login via OTP sent to your inbox.';
    } else {
      identifierType = 'mobile';
      identifierValue = this.currentEmailOrMobile;
      alreadyInUseMsg = 'Account already exists. OTP sent successfully.';
    }

    this.registerType = identifierType;
    this.currentEmailOrMobile = identifierValue;
    this.isLoginRedirectFlow = true;

    // Call LOGIN API to send OTP to the resolved channel
    this.authService.login(identifierValue, '', identifierType, 'login')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: HttpResponse<any>) => {
          this.isLoading = false;

          if (res.body?.otp_sent || res.body?.error === false) {
            this.otpSent = true;
            this.submitted = false;
            this.canResend = false;
            this.resendTimer = 60;

            setTimeout(() => {
              this.otp1Input?.nativeElement.focus();
            }, 100);

            this.registerForm.get('otp')?.setValidators([
              Validators.required,
              Validators.pattern(/^\d{6}$/)
            ]);
            this.registerForm.get('otp')?.updateValueAndValidity();

            this.startResendTimer();
            this.otpSentMessage = alreadyInUseMsg;
            this.toastr.success(alreadyInUseMsg);
          } else {
            this.toastr.error(
              res.body?.msg || 'Failed to send OTP',
              'Error!'
            );
          }
        },
        error: () => {
          this.isLoading = false;
          this.toastr.error('Failed to send OTP. Please try again.', 'Error!');
        }
      });
  }

  /**
   * Returns the identifier that's NOT currently set as the primary
   * (this.currentEmailOrMobile / this.registerType), so it can be included
   * alongside the primary in the verify/resend OTP payload. Since the same
   * OTP is delivered to both email and mobile during registration, the
   * backend needs both to verify it.
   */
  private getSecondaryIdentifier(): string {
    return this.registerType === 'email' ? this.registeredMobile : this.pendingEmail;
  }

  // Verify OTP and complete registration
  private verifyOTP(): void {
    const otp = this.otpDigits.join('');

    if (!otp || otp.length !== 6) {
      this.registerForm.get('otp')?.setErrors({ 'pattern': true });
      return;
    }

    this.isLoading = true;
    const secondaryIdentifier = this.getSecondaryIdentifier();

    if (this.isLoginRedirectFlow) {
      // Existing account (redirect_to case) — verify via the same login API
      // used for OTP login, not the register-verify API.
      this.authService.login(this.currentEmailOrMobile, '', this.registerType, 'login', otp, secondaryIdentifier)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            this.isLoading = false;
            this.handleOtpVerified(response);
          },
          error: (error) => {
            this.isLoading = false;
            this.handleOtpError(error);
          }
        });
      return;
    }

    this.authService.verifyOtpForRegistration(
      this.currentEmailOrMobile,
      otp,
      this.registerType,
      'login',
      secondaryIdentifier
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;
          this.handleOtpVerified(response);
        },
        error: (error) => {
          this.isLoading = false;
          this.handleOtpError(error);
        }
      });
  }

  private handleOtpVerified(response: HttpResponse<any>): void {
    const body = response.body;
    // Success is indicated by error === false (matching the rest of this
    // component's success checks) or a data payload being present.
    // Previously this gated storeSessionData behind response.body.token,
    // but the actual API response doesn't reliably send that flag, so user
    // data (name, email, mobile, etc.) was silently never stored.
    const isSuccess = !!body && (body.error === false || !!body.data);

    if (isSuccess) {
      this.storeSessionData(body);
      this.toastr.success('Login successful!');
      this.router.navigate(['/consumer/appointments']);
    } else {
      this.toastr.error(
        body?.msg || 'OTP verification failed.',
        'Error!'
      );
    }
  }

  /**
   * Store session data in localStorage (mirrors LoginComponent.storeSessionData)
   */
  private storeSessionData(body: any): void {
    const data = body.data;
    if (!data) {
      return;
    }

    sessionStorage.setItem('TOKEN', data.access_token);

    localStorage.setItem('TOKEN', data.access_token);
    localStorage.setItem('REFRESH_TOKEN', data.refresh_token);
    localStorage.setItem('jid', data.uid);
    localStorage.setItem('PK', data.pk);
    localStorage.setItem('first_name', data.first_name);
    localStorage.setItem('last_name', data.last_name);
    localStorage.setItem('mobile', data.mobile);
    localStorage.setItem('email', data.email);

    // T&C: Store acceptance flag from login response
    localStorage.setItem('requiresTermsAcceptance', JSON.stringify(data['requires_terms_acceptance']));

    const fullName = `${data.first_name} ${data.last_name}`.trim();
    localStorage.setItem('full_name', fullName);

    localStorage.setItem('login_type', this.registerType);

    if (this.registerType === 'email') {
      localStorage.setItem('email', this.currentEmailOrMobile);
    } else {
      localStorage.setItem('mobile', this.currentEmailOrMobile);
    }
  }

  /**
   * Handle OTP verification error
   */
  private handleOtpError(error: any): void {
    const errorMessage = error.error?.message || error.error?.msg || 'OTP verification failed. Please try again.';
    this.toastr.error(errorMessage, 'Error!');
  }

  /**
   * Resend OTP
   */
  resendOTP() {
    if (!this.canResend) return;

    this.canResend = false;
    this.resendTimer = 60;
    this.resetOtpInputs();
    this.submitted = false;

    const emailOrMobile = this.currentEmailOrMobile;
    const loginType = this.registerType;
    const secondaryIdentifier = this.getSecondaryIdentifier();

    this.authService
      .login(emailOrMobile, '', loginType, 'login', undefined, secondaryIdentifier)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: HttpResponse<any>) => {
          if (res.body?.otp_sent) {
            const destination = loginType === 'email' ? 'email' : 'mobile number';
            this.toastr.success(
              res.body.msg || `OTP resent to your ${destination}`,
              'Success!'
            );
            this.startResendTimer();
          } else {
            this.toastr.error('Failed to resend OTP', 'Error!');
          }
        },
        error: () => {
          this.toastr.error('Failed to resend OTP', 'Error!');
        }
      });
  }

  /**
   * Cancel OTP verification and go back
   */
  cancelOTP() {
    this.otpSent = false;
    this.submitted = false;
    this.isLoginRedirectFlow = false;
    this.otpSentMessage = '';
    this.resetOtpInputs();
    this.registerForm.get('otp')?.reset();
    this.stopResendTimer();
  }

  /**
   * Start resend timer countdown
   */
  private startResendTimer() {
    this.timerInterval = setInterval(() => {
      this.resendTimer--;
      if (this.resendTimer <= 0) {
        this.canResend = true;
        clearInterval(this.timerInterval);
      }
    }, 1000);
  }

  /**
   * Stop resend timer
   */
  private stopResendTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.resendTimer = 60;
    this.canResend = false;
  }

  // Reset OTP inputs
  resetOtpInputs() {
    this.otpDigits = ['', '', '', '', '', ''];
  }

  /**
   * Handle OTP box value changes (typing, autofill, IME/mobile keyboards).
   * Uses the `input` event rather than `keyup` because virtual/mobile
   * keyboards don't reliably fire keyup with usable key info, which is
   * why auto-advance to the next box could silently fail.
   */
  onOtpInput(event: any, index: number, nextInput: any) {
    const input = event.target;
    let value = input.value;

    if (value && !/^\d$/.test(value)) {
      value = value.replace(/\D/g, '').slice(-1);
      input.value = value;
    }

    this.otpDigits[index - 1] = value;

    const otpValue = this.otpDigits.join('');
    this.registerForm.patchValue({ otp: otpValue });

    if (value && nextInput) {
      nextInput.focus();
    }
  }

  /**
   * Backspace/arrow navigation (`index` is 1-based). Backspace is handled here
   * rather than on `input` because clearing an already-empty box fires no
   * `input` event. One press always deletes one digit *and* steps the cursor
   * back, so clearing the code box by box never leaves the cursor parked on
   * the box that was just emptied.
   */
  onOtpKeydown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;
    const prev = input.previousElementSibling as HTMLInputElement | null;
    const next = input.nextElementSibling as HTMLInputElement | null;

    if (event.key === 'Backspace') {
      event.preventDefault();

      // Delete this box's digit, or the previous one when this box is empty.
      if (this.otpDigits[index - 1]) {
        this.otpDigits[index - 1] = '';
        input.value = '';
      } else if (prev) {
        this.otpDigits[index - 2] = '';
        prev.value = '';
      }

      this.registerForm.patchValue({ otp: this.otpDigits.join('') });
      prev?.focus();
    } else if (event.key === 'ArrowLeft' && prev) {
      event.preventDefault();
      prev.focus();
    } else if (event.key === 'ArrowRight' && next) {
      event.preventDefault();
      next.focus();
    }
  }

  /**
   * Handle OTP paste event
   */
  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const otpArray = pastedData.replace(/\D/g, '').slice(0, 6).split('');

    for (let i = 0; i < otpArray.length; i++) {
      this.otpDigits[i] = otpArray[i];
    }

    const otpValue = this.otpDigits.join('');
    this.registerForm.patchValue({ otp: otpValue });

    const focusIndex = Math.min(otpArray.length, 5);
    const nextInput = document.querySelector(
      `.otp-box:nth-child(${focusIndex + 1})` as any
    ) as HTMLInputElement;
    if (nextInput) {
      nextInput.focus();
    }
  }

  /**
   * Restrict text input (remove extra spaces)
   */
  restricttextSpace(event: any) {
    let inputValue: string = event.target.value;
    inputValue = inputValue.replace(/^\s+/, '');
    inputValue = inputValue.replace(/\s{2,}/g, ' ');
    event.target.value = inputValue;
  }

  private trimFormFields(): void {
    const emailControl = this.registerForm.get('email');
    if (emailControl) {
      emailControl.setValue(emailControl.value?.trim().toLowerCase() ?? '');
    }
    const mobileControl = this.registerForm.get('mobile');
    if (mobileControl) {
      mobileControl.setValue(mobileControl.value?.replace(/^0+/, '').trim() ?? '');
    }
    const firstNameControl = this.registerForm.get('firstName');
    if (firstNameControl) {
      firstNameControl.setValue(firstNameControl.value?.trim() ?? '');
    }
    const lastNameControl = this.registerForm.get('lastName');
    if (lastNameControl) {
      lastNameControl.setValue(lastNameControl.value?.trim() ?? '');
    }
  }
}